import { ok, err, type Result } from 'neverthrow';
import { toast } from '@cotera/client/app/components/ui';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { QueryClient } from '@tanstack/react-query';

export type MovableResource = { type: ResourceType; id: string };

/** Only the client surface the move actually calls. */
export type MoveToFolderClient = {
  resource: {
    folder: Pick<
      TenantedClient['resource']['folder'],
      'moveToFolder' | 'unassignFromFolder'
    >;
  };
};

/** The bit of the folder list model used to optimistically hide moved rows. */
export type FolderContentsOptimisticModel = {
  removeItemsOptimistically(ids: string[]): () => void;
};

/**
 * Moves resources into a folder (or to the top level when `folderId` is null),
 * with optimistic removal from the folder list, query invalidation, and toasts.
 * Shared by the Move-to-folder action (modal picker) and library drag-and-drop
 * so both paths behave identically.
 */
export async function moveResourcesToFolder(args: {
  client: MoveToFolderClient;
  queryClient: Pick<QueryClient, 'invalidateQueries'>;
  orgId: string;
  resources: MovableResource[];
  /** Target folder id, or `null` to move out to the top level. */
  folderId: string | null;
  /** Ids to optimistically remove from the current folder list while moving. */
  optimisticIds: string[];
  folderPageModel: FolderContentsOptimisticModel | null;
}): Promise<Result<void, string>> {
  const {
    client,
    queryClient,
    orgId,
    resources,
    folderId,
    optimisticIds,
    folderPageModel,
  } = args;

  if (resources.length === 0) {
    return err('No resource in scope');
  }

  const toTopLevel = folderId === null;
  const rollback =
    folderPageModel?.removeItemsOptimistically(optimisticIds) ?? (() => {});

  for (const resource of resources) {
    const result =
      folderId === null
        ? await client.resource.folder.unassignFromFolder({
            entityType: resource.type,
            entityId: resource.id,
          })
        : await client.resource.folder.moveToFolder({
            type: resource.type,
            id: resource.id,
            folderId,
          });

    if (result.isErr()) {
      rollback();
      const fallback = toTopLevel
        ? 'Failed to move to top level'
        : 'Failed to move to folder';
      const msg =
        result.error.t === 'application'
          ? result.error.err.message ?? fallback
          : fallback;
      toast.error(msg);
      return err(msg);
    }
  }

  const selectedResourceTypes = [...new Set(resources.map((r) => r.type))];
  for (const resourceType of selectedResourceTypes) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.list({
        orgId,
        type: resourceType,
        limit: 50,
      }),
    });
  }
  void queryClient.invalidateQueries({
    queryKey: queryKeys.resource.folderTree({ orgId }),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.resource.folderList({ orgId }),
  });

  const destinationLabel = toTopLevel ? 'top level' : 'folder';
  toast.success(
    resources.length > 1
      ? `Moved ${resources.length} items to ${destinationLabel}`
      : `Moved to ${destinationLabel}`
  );

  return ok(undefined);
}
